/**
 * TourRegistry
 *
 * Role-based tour definitions. Each step targets a real DOM element via a
 * `target` CSS selector (usually `[data-tour="<name>"]`) or is `null` for a
 * "screen-center" informative step.
 *
 * Fields per step:
 *  - id: stable string id
 *  - target: CSS selector or null
 *  - title, body: strings
 *  - route (optional): path the tour should navigate to before showing
 *  - placement: 'top' | 'bottom' | 'left' | 'right' | 'auto' (default 'auto')
 *  - waitFor (optional): ms to wait for target after route change (default 1500)
 *  - onEnter (optional): fn(navigate) called on step enter
 */

const commonEnd = {
  id: 'end',
  target: null,
  title: '¡Listo!',
  body: 'Ya conoces las principales áreas de SimuClinic. Puedes repetir este recorrido cuando quieras desde el menú de ayuda (?).',
};

const alumnoTour = {
  id: 'onboarding-alumno',
  version: '1',
  role: 'alumno',
  title: 'Recorrido para alumnos',
  steps: [
    {
      id: 'welcome',
      target: null,
      title: 'Bienvenido a SimuClinic',
      body: 'Te acompañaremos para que entiendas cómo tomar decisiones clínicas, ver la evolución del paciente y aprovechar el debriefing. Duración estimada: 5 minutos.',
    },
    { id: 'sidebar-dashboard', route: '/app/dashboard', target: '[data-testid="nav-dashboard"]', title: 'Tu Dashboard', body: 'Aquí ves tu progreso general, últimas simulaciones y accesos rápidos.', placement: 'right' },
    { id: 'sidebar-scenarios', target: '[data-testid="nav-escenarios"]', title: 'Escenarios', body: 'Desde aquí revisas los casos clínicos disponibles con especialidad, dificultad y objetivos.', placement: 'right' },
    { id: 'sidebar-evaluations', target: '[data-testid="nav-evaluaciones"]', title: 'Evaluaciones', body: 'Revisa el detalle de cada simulación evaluada por SimuClinic.', placement: 'right' },
    { id: 'sidebar-history', target: '[data-testid="nav-mi-historial"]', title: 'Mi Historial', body: 'Todas tus simulaciones anteriores, con puntajes y evolución en el tiempo.', placement: 'right' },
    { id: 'user-menu', target: '[data-tour="user-menu"]', title: 'Ayuda siempre a mano', body: 'Puedes reabrir este recorrido cuando quieras desde el botón ? o desde tu menú de usuario.', placement: 'left' },
    commonEnd,
  ],
};

const instructorTour = {
  id: 'onboarding-instructor',
  version: '1',
  role: 'instructor',
  title: 'Recorrido para instructores',
  steps: [
    { id: 'welcome', target: null, title: 'Bienvenido al entorno del Instructor', body: 'Prepararás escenarios, supervisarás en vivo, intervendrás en la simulación y guiarás el debriefing.' },
    { id: 'sidebar-students', route: '/app/dashboard', target: '[data-testid="nav-mis-estudiantes"]', title: 'Mis Estudiantes', body: 'Estudiantes asociados a tus cursos y establecimientos.', placement: 'right' },
    { id: 'sidebar-supervision', target: '[data-testid="nav-supervisión"]', title: 'Supervisión en vivo', body: 'Observa simulaciones activas, interviene en el estado del paciente, envía pistas y controla el tiempo.', placement: 'right' },
    { id: 'sidebar-scenarios', target: '[data-testid="nav-escenarios"]', title: 'Escenarios', body: 'Aquí revisas y creas los casos clínicos que tus alumnos resolverán.', placement: 'right' },
    { id: 'sidebar-evaluations', target: '[data-testid="nav-evaluaciones"]', title: 'Evaluaciones', body: 'Revisa las evaluaciones automáticas y complementa con tu criterio docente.', placement: 'right' },
    commonEnd,
  ],
};

const adminTour = {
  id: 'onboarding-admin',
  version: '2',
  role: 'admin',
  title: 'Recorrido para administradores',
  steps: [
    { id: 'welcome', target: null, title: 'Bienvenido, Administrador', body: 'Gestionarás usuarios, establecimientos, branding, cupos y verás la actividad de tu institución.' },
    { id: 'sidebar-org', route: '/app/dashboard', target: '[data-tour="nav-organizacion"]', title: 'Organización', body: 'Aquí gestionas Establecimientos, Usuarios, Roles, Niveles y Auditoría.', placement: 'right' },
    // v10.4.6: sub-menús con group → InteractiveTour dispara tour:open-group antes de mostrar
    { id: 'sidebar-establishments', group: 'organizacion', target: '[data-testid="nav-establecimientos"]', title: 'Establecimientos', body: 'Crea sedes, asigna plan, configura subdominio y branding.', placement: 'right' },
    { id: 'sidebar-users', group: 'organizacion', target: '[data-testid="nav-usuarios"]', title: 'Usuarios', body: 'Aprueba solicitudes de registro, asigna roles y controla acceso.', placement: 'right' },
    { id: 'sidebar-maintainers-parent', target: '[data-tour="nav-mantenedores"]', title: 'Mantenedores Clínicos', body: 'Medicamentos, vías, presentaciones, acciones, ritmos ECG y configuración del establecimiento.', placement: 'right' },
    // v10.4.6: al expandir mantenedores mostramos también sus hijos clave
    { id: 'sidebar-medications', group: 'mantenedores', target: '[data-testid="nav-medicamentos"]', title: 'Medicamentos', body: 'Catálogo central de medicamentos usados en las simulaciones.', placement: 'right' },
    commonEnd,
  ],
};

const superAdminTour = {
  id: 'onboarding-superadmin',
  version: '2',
  role: 'super_admin',
  title: 'Recorrido para Super Admin',
  steps: [
    { id: 'welcome', target: null, title: 'Panel Global SaaS', body: 'Como super_admin controlas todos los tenants: planes, comercial, analytics y configuración global.' },
    { id: 'sidebar-saas', route: '/app/dashboard', target: '[data-tour="nav-saas"]', title: 'Gestión SaaS', body: 'Planes, Comercial, Analytics y Configuración global.', placement: 'right' },
    { id: 'sidebar-establishments', group: 'organizacion', target: '[data-testid="nav-establecimientos"]', title: 'Establecimientos', body: 'Vista global de todas las instituciones. Puedes asignar planes y activar branding.', placement: 'right' },
    { id: 'sidebar-difusion', target: '[data-tour="nav-difusion"]', title: 'Difusión', body: 'Demo comercial, videos institucionales y cargas masivas.', placement: 'right' },
    { id: 'user-menu', target: '[data-tour="user-menu"]', title: 'Personalización', body: 'Personaliza el orden del menú y accede al modo soporte desde tu menú de usuario.', placement: 'left' },
    commonEnd,
  ],
};

const simulationMiniTour = {
  id: 'sim-first-time',
  version: '2',
  role: '*',
  title: 'Recorrido dentro de una simulación',
  steps: [
    { id: 'welcome', target: null, title: 'Estás en tu primera simulación', body: 'Te mostraremos rápidamente los 4 controles principales: monitor, anamnesis, acciones y medicamentos. Duración: 60 segundos.' },
    { id: 'monitor', target: '[data-tour="simulation-monitor"]', title: 'Monitor de signos vitales', body: 'Representa el estado fisiológico actual del paciente: FC, PA, SpO₂, FR y temperatura. Los valores cambian con tus decisiones y con la evolución del escenario.', placement: 'right' },
    // v10.4.6: preClick activa el tab de Anamnesis para que su contenido esté visible
    { id: 'anamnesis', preClick: '[data-tour="simulation-anamnesis"]', target: '[data-tour="simulation-anamnesis"]', title: 'Anamnesis', body: 'Pregunta al paciente para obtener antecedentes y contextualizar el caso antes de tomar decisiones.', placement: 'top' },
    // v10.4.6: preClick activa el tab de Acciones en el panel derecho
    { id: 'actions', preClick: '[data-tour="simulation-actions"]', target: '[data-tour="simulation-actions"]', title: 'Acciones clínicas', body: 'Intervenciones sobre el paciente: soporte vital, vía aérea, acceso vascular, procedimientos y cuidados. Cada acción queda registrada para la evaluación.', placement: 'top' },
    // v10.4.6: preClick activa el tab de Medicamentos
    { id: 'medications', preClick: '[data-tour="simulation-medications"]', target: '[data-tour="simulation-medications"]', title: 'Medicamentos', body: 'Administra medicamentos definiendo presentación, dosis, unidad y vía. Las decisiones farmacológicas también son evaluadas.', placement: 'top' },
    commonEnd,
  ],
};

const TOURS = {
  alumno: alumnoTour,
  instructor: instructorTour,
  admin: adminTour,
  super_admin: superAdminTour,
};

export function getTourForRole(role) {
  return TOURS[role] || alumnoTour;
}

export function getMiniTour(id) {
  const MINI = { 'sim-first-time': simulationMiniTour };
  return MINI[id] || null;
}

export const ALL_TOURS = { ...TOURS, 'sim-first-time': simulationMiniTour };
